import { Home, Building2, Sparkles, CalendarClock, Repeat, Truck } from "lucide-react";

export const BUSINESS = {
  name: "Wisp Home Cleaning",
  phone: "289-799-2690",
  phoneNote: "Call or Text",
  phoneHref: "tel:+12897992690",
  email: "Office@wisphomecleaning.ca",
  emailHref: "mailto:Office@wisphomecleaning.ca",
  hours: [
    { day: "Monday – Friday", time: "8:30am – 4:30pm" },
    { day: "Weekends & Holidays", time: "Closed" },
  ],
  region: "Hamilton & surrounding communities",
  since: "2019",
  facebook: "https://www.facebook.com/wisphomecleaning.ca/",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Areas We Serve", to: "/areas" },
  { label: "Meet Wisp", to: "/about" },
  { label: "Reviews", to: "/reviews" },
  { label: "Contact", to: "/contact" },
];

export const GOOGLE = {
  rating: "4.9",
  count: "28",
  url: "https://www.google.com/maps/place/Wisp+Home+Cleaning+Services/@43.2635507,-79.880177,10z/data=!4m8!3m7!1s0x882c99fbe18818c5:0x7a2f16bba43219b0!8m2!3d43.2635507!4d-79.880177!9m1!1b1!16s%2Fg%2F11fm1691cc",
};

export const SERVICES = [
  {
    icon: Home,
    title: "Residential Cleaning",
    summary:
      "Thorough, detail-focused cleaning for larger homes — kitchens, bathrooms, bedrooms and living spaces left fresh, tidy and sparkling.",
    points: ["Whole-home deep & maintenance cleans", "Kitchen & bathroom sanitizing", "Dusting, floors, and finishing touches"],
    image: "/svc-residential.jpg",
  },
  {
    icon: Building2,
    title: "Small-Office Cleaning",
    summary:
      "Reliable, discreet cleaning for small offices and workspaces — a spotless, welcoming environment for your team and clients.",
    points: ["Desks, common areas & kitchens", "Washroom sanitizing & restocking", "Flexible before/after-hours scheduling"],
    image: "/svc-office.jpg",
  },
];

export const SERVICE_TYPES = [
  { icon: Sparkles, title: "Deep Cleaning", desc: "A comprehensive top-to-bottom reset for your home or office." },
  { icon: Repeat, title: "Recurring Cleaning", desc: "Weekly, bi-weekly or monthly visits that keep your space consistently pristine." },
  { icon: CalendarClock, title: "One-Time Cleaning", desc: "A single detailed clean for special occasions or a seasonal refresh." },
  { icon: Truck, title: "Move In / Move Out", desc: "Detailed cleaning to hand over — or move into — a spotless space." },
];

export const AREAS = [
  { name: "Upper Stoney Creek", blurb: "Larger family homes across the mountain brow with easy recurring scheduling." },
  { name: "Heritage Green", blurb: "Established neighbourhoods that value dependable, detail-focused cleaning." },
  { name: "Summit Park & Hannon", blurb: "Newer, spacious homes ideal for weekly and bi-weekly maintenance cleans." },
  { name: "Binbrook", blurb: "Growing community of larger properties and busy households." },
  { name: "Ancaster", blurb: "Executive and larger homes where quality and trust come first." },
  { name: "Dundas", blurb: "Character homes and modern residences throughout the valley." },
  { name: "Waterdown", blurb: "Family neighbourhoods looking for a reliable recurring cleaning partner." },
  { name: "Burlington", blurb: "Homes and small offices seeking a polished, professional finish." },
];

export const TRUST_SIGNALS = [
  { title: "Multiple Award-Winning", desc: "Recognized by the Hamilton community over multiple years for our home cleaning." },
  { title: "Registered, Insured & Bondable", desc: "Fully registered and insured, bondable, with police checks available when required." },
  { title: "Professionally Trained", desc: "Trained in WHMIS, GBAC, Workplace Health & Safety and AODA, with years of hands-on experience." },
  { title: "Serving Hamilton Since 2019", desc: "Locally owned and operated, and proud to care for our neighbours' homes." },
];

// The heart of Wisp — what guides everything we do.
export const HEART = [
  "People come first",
  "Respecting clients and their personal spaces",
  "Listening carefully and communicating clearly",
  "Caring about the details",
  "Accommodating each household's priorities where possible",
  "Consistent, straightforward and trustworthy service",
  "Giving you more time, comfort and one less thing to carry",
];

export const FEATURED_QUOTES = [
  { quote: "We love coming home and seeing their small touches everywhere.", name: "Halina" },
  { quote: "It shows how much she cares for the work and her clients.", name: "Google Customer" },
];

export const FAQS = [
  {
    q: "How do you determine your pricing?",
    a: "Every home is wonderfully different, so your price depends on things like the size and layout of your home, its current condition, how often you'd like us to visit, and exactly what you'd like us to focus on. Rather than guess with a one-size-fits-all number, we get to know your home first, so your quote is fair, accurate, and truly yours.",
  },
  {
    q: "Why do you do a free in-home visit?",
    a: "It's the best way for us to get things right from the start. Seeing your home in person lets us understand your space, hear what matters most to you, and give honest, accurate pricing with no surprises. Just as importantly, it's a chance for us to meet, so you feel comfortable and confident welcoming us in. No pressure, no obligation — just a friendly conversation.",
  },
  {
    q: "What's included in a clean?",
    a: "We tailor every visit to your home, but a typical clean covers kitchens, bathrooms, bedrooms and living areas — dusting, floors, surfaces and sanitizing — plus thoughtful finishing touches like arranging cushions, straightening towels and tidying beds. We'll leave your décor and personal belongings right where they are. Please note our cleans don't include laundry or dishes.",
  },
  {
    q: "Do you offer recurring cleaning?",
    a: "Yes! Many clients book weekly, bi-weekly or monthly visits to keep their home consistently fresh, and we also offer one-time deep cleans and move in/out cleaning. If more than one visit in a row is skipped, a small additional fee may apply, as it takes a little extra time to bring your home back to its maintenance level.",
  },
  {
    q: "How and when do I pay?",
    a: "Payment is due on the day of your service, at least an hour before your appointment. You're welcome to leave cash in a clearly marked envelope on the kitchen counter, send an e-transfer to Office@wisphomecleaning.ca, or pay by credit card through the secure link on your invoice.",
  },
  {
    q: "What time will you arrive?",
    a: "We kindly ask that you allow a 2-hour arrival window, since things like weather and traffic can shift our timing (good news — we often arrive earlier than expected!). We'll always do our best to keep you posted, and you're welcome to reach out to us anytime for an update.",
  },
  {
    q: "Do I need to be home during the cleaning?",
    a: "Not at all. Many clients give us access while they're at work or out enjoying their day. Our technicians are courteous and respectful, and while we're in your home, we're there for one reason: to clean it beautifully. We treat your space with care whether you're there or not.",
  },
  {
    q: "What about my pets?",
    a: "We adore meeting your furry family members — they're often the highlight of our day! That said, we're focused on cleaning and can't be responsible for them, so if your pet might get anxious, over-excited, protective, or try to slip outside, please pop them in a crate or a safe room before we arrive.",
  },
  {
    q: "Is Wisp licensed and insured?",
    a: "Absolutely. Wisp is a professional home cleaning service, locally owned and operated since 2019 — registered and fully insured, bondable, with police record checks available when required. We're professionally trained in WHMIS, GBAC, Workplace Health & Safety and AODA, with several years of hands-on experience, and we're proud to be a multiple award-winning team backed by strong Google and Facebook reviews. Your peace of mind matters to us as much as a spotless home.",
  },
];

// Real Google reviews from Wisp Home Cleaning's Google listing.
export const REVIEWS = [
  {
    name: "Kimberly G.",
    location: "Google Review",
    rating: 5,
    text: "The team at Wisp has been incredible. Sue and her team were recommended by a friend, and I'm so happy we locked them in for bi-weekly cleans. They do a fantastic job — very thorough and get into all the nooks. They're flexible and focus on parts of the home if there's a special need, which as a mom of a 2 year old I appreciate very much. Most importantly, the team is friendly and responsive. Our cat also loves them, which is a plus when we're unable to be home during cleanings. Would 10/10 recommend!",
  },
  {
    name: "David B.",
    location: "Google Review",
    rating: 5,
    text: "The Wisp Team have been great! First, recently asked Wisp to help with a construction clean before moving back into our home — the quality of work has blown us away. Highly recommend this service! The team is professional and do a great deep clean of our home.",
  },
  {
    name: "Fatima G.",
    location: "Google Review",
    rating: 5,
    text: "I highly recommend this service. They are professional since the first approach. They explained to me in detail the work to be done. On the day of the appointment they were very punctual, they checked the property and explained again what they were going to do and how long it would take. When I got back, the apartment was spotless, everything looks like new. They did a great job with the details. I can see dedication and care. We thank you very much — now my husband and me are ready to enjoy our new home.",
  },
  {
    name: "Halina",
    location: "Google Review",
    rating: 5,
    text: "We hired the Wisp team over a year ago and our lives have never been easier. Sue, Jen and Rebecca are without a doubt respectful, honest and very attentive to detail. We love coming home and seeing their small touches everywhere, be it a folded corner on the paper towels or some folded clothes that were left behind. I would recommend these ladies to anyone who is looking for a cleaning service.",
  },
  {
    name: "Google Customer",
    location: "Verified review",
    rating: 5,
    text: "I was not able to schedule a cleaning with Wisp (our fault as it is a last minute cleaning requirement), but when I did call to inquire, Sue took time out of her day to go over some helpful tips and tricks in case I had to do the cleaning myself. She didn't have to do that, but it shows how much she cares for the work and her clients — even if we couldn't book with her. Because of this outstanding service, we will be keeping her in mind for future services! Thanks Sue, you are a true asset within that company, and a pleasure to speak with!",
  },
];
